import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { baseAxios, Context } from "../utils/config";
import { shouldPromptOvernightCloseout } from "../utils/registerCloseoutPrompt";
import RegisterCloseoutDialog from "../pages/dashboard/RegisterCloseoutDialog";
import RegisterOpenDialog from "../pages/dashboard/RegisterOpenDialog";
import RegisterCashOutDialog from "../pages/dashboard/RegisterCashOutDialog";

const SELECT_LOCATION_MESSAGE =
  "Please select a specific location before opening or closing the register.";

const formatDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Global register modal manager:
 * - Handles open/close dialogs when requested from the Scheduler (or /till when closed).
 * - Requests closeout (dismissible) if the register is still open at 8am local
 *   the morning after it was opened.
 */
export default function RegisterDialogManager() {
  const urlLocation = useLocation();
  const { location: globalLocation } = useContext(Context);

  const selectedLocationId = globalLocation?.location_id;
  const hasSpecificLocation = Number(selectedLocationId) > 0;
  const locationName =
    hasSpecificLocation && globalLocation?.location_name
      ? globalLocation.location_name
      : "";
  const statusRequestIdRef = useRef(0);
  const tillPromptKeyRef = useRef(null);
  const overnightPromptedPeriodIdRef = useRef(null);

  const [status, setStatus] = useState(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [closeoutDialogOpen, setCloseoutDialogOpen] = useState(false);
  const [openingDialogOpen, setOpeningDialogOpen] = useState(false);
  const [dialogAccounted, setDialogAccounted] = useState(null);

  const fetchStatus = useCallback(async () => {
    const requestId = ++statusRequestIdRef.current;
    if (!hasSpecificLocation) {
      setStatus(null);
      setStatusLoaded(true);
      return null;
    }
    try {
      const res = await baseAxios.get("/register/status", {
        params: { locationId: selectedLocationId },
      });
      if (requestId === statusRequestIdRef.current) {
        setStatus(res.data);
        setStatusLoaded(true);
      }
      return res.data;
    } catch {
      if (requestId === statusRequestIdRef.current) {
        setStatus(null);
        setStatusLoaded(true);
      }
      return null;
    }
  }, [hasSpecificLocation, selectedLocationId]);

  // Close any in-progress dialogs when the header location changes so we never
  // open/close the wrong salon's register. Runs before fetchStatus on the same
  // commit so Till can wait for a fresh status instead of a stale loaded flag.
  useEffect(() => {
    setOpenDialogOpen(false);
    setCloseoutDialogOpen(false);
    setOpeningDialogOpen(false);
    setDialogAccounted(null);
    setStatus(null);
    setStatusLoaded(false);
    overnightPromptedPeriodIdRef.current = null;
  }, [selectedLocationId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const handler = () => fetchStatus();
    window.addEventListener("register-status-updated", handler);
    return () => window.removeEventListener("register-status-updated", handler);
  }, [fetchStatus]);

  // Light polling so UI stays correct even without navigation.
  useEffect(() => {
    if (!hasSpecificLocation) return;
    const id = window.setInterval(fetchStatus, 60_000);
    return () => window.clearInterval(id);
  }, [fetchStatus, hasSpecificLocation]);

  const isRegisterOpen = status?.isOpen ?? false;
  const currentPeriod = status?.currentPeriod ?? null;
  const lastClosedPeriod = status?.lastClosedPeriod ?? null;
  const accounted = status?.accounted ?? null;
  const displayAccounted =
    (closeoutDialogOpen || openingDialogOpen) && dialogAccounted
      ? dialogAccounted
      : accounted;

  const previousCashOnHand =
    currentPeriod?.startingCash ?? lastClosedPeriod?.cashInDrawer ?? 0;
  const accountedCashSales = displayAccounted?.cash ?? 0;
  const totalCashOuts = displayAccounted?.totalCashOuts ?? 0;
  const totalPayIns = displayAccounted?.totalPayIns ?? 0;
  const currentCashAvailable = displayAccounted?.currentCashAvailable ?? 0;
  const expectedCloseoutTotals = useMemo(
    () => ({
      creditCards: displayAccounted?.creditCards ?? 0,
      checks: displayAccounted?.checks ?? 0,
      giftCards: displayAccounted?.giftCards ?? 0,
    }),
    [
      displayAccounted?.checks,
      displayAccounted?.creditCards,
      displayAccounted?.giftCards,
    ]
  );

  const openCloseoutWithFreshStatus = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await fetchStatus();
      setDialogAccounted(fresh?.accounted ?? null);
      setOpenDialogOpen(false);
      setOpeningDialogOpen(false);
      setCloseoutDialogOpen(true);
    } finally {
      setLoading(false);
    }
  }, [fetchStatus]);

  const openOpeningWithFreshStatus = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await fetchStatus();
      setDialogAccounted(fresh?.accounted ?? null);
      setOpenDialogOpen(false);
      setCloseoutDialogOpen(false);
      setOpeningDialogOpen(true);
    } finally {
      setLoading(false);
    }
  }, [fetchStatus]);

  const requestOpening = useCallback(() => {
    if (!hasSpecificLocation) {
      toast.error(SELECT_LOCATION_MESSAGE);
      return;
    }
    if (!isRegisterOpen) {
      toast.error("Please open the register first.");
      return;
    }
    setOpenDialogOpen(false);
    setCloseoutDialogOpen(false);
    void openOpeningWithFreshStatus();
  }, [hasSpecificLocation, isRegisterOpen, openOpeningWithFreshStatus]);

  const requestOpen = useCallback(() => {
    if (!hasSpecificLocation) {
      toast.error(SELECT_LOCATION_MESSAGE);
      return;
    }
    setCloseoutDialogOpen(false);
    setOpeningDialogOpen(false);
    setOpenDialogOpen(true);
  }, [hasSpecificLocation]);

  const requestToggle = useCallback(() => {
    if (!hasSpecificLocation) {
      toast.error(SELECT_LOCATION_MESSAGE);
      return;
    }
    if (!isRegisterOpen) {
      requestOpen();
    }
  }, [hasSpecificLocation, isRegisterOpen, requestOpen]);

  useEffect(() => {
    const handler = () => requestOpening();
    window.addEventListener("register-opening-request", handler);
    return () =>
      window.removeEventListener("register-opening-request", handler);
  }, [requestOpening]);

  useEffect(() => {
    const handler = () => requestOpen();
    window.addEventListener("register-open-request", handler);
    return () => window.removeEventListener("register-open-request", handler);
  }, [requestOpen]);

  useEffect(() => {
    const handler = () => void openCloseoutWithFreshStatus();
    window.addEventListener("register-closeout-request", handler);
    return () =>
      window.removeEventListener("register-closeout-request", handler);
  }, [openCloseoutWithFreshStatus]);

  useEffect(() => {
    const handler = () => requestToggle();
    window.addEventListener("register-toggle-request", handler);
    return () => window.removeEventListener("register-toggle-request", handler);
  }, [requestToggle]);

  // If closeout was forgotten overnight, request it at 8am local the next morning.
  // Dismissible, and not re-opened on the 60s poll for the same period.
  useEffect(() => {
    if (!statusLoaded || !isRegisterOpen || !currentPeriod?.id) return;
    if (openDialogOpen || openingDialogOpen) return;
    if (
      !shouldPromptOvernightCloseout({
        isOpen: isRegisterOpen,
        openedAt: currentPeriod.openedAt,
      })
    ) {
      return;
    }

    const periodId = currentPeriod.id;
    if (overnightPromptedPeriodIdRef.current === periodId) return;
    overnightPromptedPeriodIdRef.current = periodId;
    if (!closeoutDialogOpen) {
      void openCloseoutWithFreshStatus();
    }
  }, [
    statusLoaded,
    isRegisterOpen,
    currentPeriod?.id,
    currentPeriod?.openedAt,
    openDialogOpen,
    openingDialogOpen,
    closeoutDialogOpen,
    openCloseoutWithFreshStatus,
  ]);

  // Till nav loads Scheduler; only prompt to open when the register is still closed.
  useEffect(() => {
    if (urlLocation.pathname !== "/till") return;
    if (!statusLoaded) return;
    const promptKey = `${urlLocation.key}:${selectedLocationId ?? ""}`;
    if (tillPromptKeyRef.current === promptKey) return;
    tillPromptKeyRef.current = promptKey;
    if (!isRegisterOpen) {
      requestOpen();
    }
  }, [
    urlLocation.pathname,
    urlLocation.key,
    selectedLocationId,
    statusLoaded,
    isRegisterOpen,
    requestOpen,
  ]);

  const handleOpenConfirm = async ({ cashOnHand }) => {
    if (!hasSpecificLocation) {
      toast.error(SELECT_LOCATION_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      await baseAxios.post("/register/open", {
        locationId: selectedLocationId,
        cashOnHand,
      });
      setOpenDialogOpen(false);
      toast.success(
        locationName
          ? `Register opened for ${locationName}.`
          : "Register opened for today."
      );
      await fetchStatus();
      window.dispatchEvent(new Event("register-status-updated"));
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || "Failed to open register";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpeningConfirm = async ({ amount, type }) => {
    if (!hasSpecificLocation) {
      toast.error(SELECT_LOCATION_MESSAGE);
      return;
    }
    const movementType = type === "pay_in" ? "pay_in" : "payout";
    const movementLabel = movementType === "pay_in" ? "Pay in" : "Payout";
    setLoading(true);
    try {
      await baseAxios.post("/register/cash-out", {
        locationId: selectedLocationId,
        amount,
        type: movementType,
      });
      setOpeningDialogOpen(false);
      setDialogAccounted(null);
      toast.success(
        locationName
          ? `${movementLabel} of $${amount.toFixed(2)} recorded for ${locationName}.`
          : `${movementLabel} of $${amount.toFixed(2)} recorded.`
      );
      await fetchStatus();
      window.dispatchEvent(new Event("register-status-updated"));
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.message ||
        `Failed to record ${movementLabel.toLowerCase()}`;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseConfirm = async (payload) => {
    if (!hasSpecificLocation) {
      toast.error(SELECT_LOCATION_MESSAGE);
      return;
    }
    if (!currentPeriod?.id) {
      toast.error("No open register period found.");
      return;
    }
    setLoading(true);
    try {
      await baseAxios.post(`/register/close/${currentPeriod.id}`, {
        cashInDrawer: payload.cashInDrawer,
        creditCards: payload.creditCards,
        checks: payload.checks,
        giftCards: payload.giftCards,
      });
      setCloseoutDialogOpen(false);
      toast.success(
        locationName ? `Register closed for ${locationName}.` : "Register closed."
      );
      await fetchStatus();
      window.dispatchEvent(new Event("register-status-updated"));
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || "Failed to close register";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // DevExtreme Drawer can sit above MUI modal; force register modals above everything.
  const MODAL_Z_INDEX = 20000;

  return (
    <>
      <RegisterCloseoutDialog
        open={closeoutDialogOpen}
        onClose={() => {
          setCloseoutDialogOpen(false);
          setDialogAccounted(null);
        }}
        onConfirm={handleCloseConfirm}
        openedAt={formatDateTime(currentPeriod?.openedAt)}
        closingAt={formatDateTime(new Date().toISOString())}
        previousCashOnHand={previousCashOnHand}
        accountedCashSales={accountedCashSales}
        totalCashOuts={totalCashOuts}
        totalPayIns={totalPayIns}
        expectedTotals={expectedCloseoutTotals}
        loading={loading}
        modalZIndex={MODAL_Z_INDEX}
        locationName={locationName}
      />

      <RegisterCashOutDialog
        open={openingDialogOpen}
        onClose={() => {
          setOpeningDialogOpen(false);
          setDialogAccounted(null);
        }}
        onConfirm={handleOpeningConfirm}
        currentCashAvailable={currentCashAvailable}
        loading={loading}
        modalZIndex={MODAL_Z_INDEX}
        locationName={locationName}
      />

      <RegisterOpenDialog
        open={openDialogOpen}
        onClose={() => setOpenDialogOpen(false)}
        onConfirm={handleOpenConfirm}
        openedAt={formatDateTime(new Date().toISOString())}
        initialCashOnHand={
          lastClosedPeriod?.cashInDrawer != null ? lastClosedPeriod.cashInDrawer : 0
        }
        loading={loading}
        modalZIndex={MODAL_Z_INDEX}
        locationName={locationName}
      />
    </>
  );
}
